import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export const PALETTE = ["#f97316", "#22c55e", "#3b82f6", "#a855f7", "#eab308", "#ec4899", "#14b8a6", "#ef4444"];

export const CategoryDialog = ({ trigger, title, initial = {}, parentId = null, onSubmit }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(initial.name || "");
  const [proposed, setProposed] = useState(initial.proposed ?? "");
  const [color, setColor] = useState(initial.color || PALETTE[0]);
  const [threshold, setThreshold] = useState(initial.alert_threshold ?? 80);

  React.useEffect(() => {
    if (open) {
      setName(initial.name || "");
      setProposed(initial.proposed ?? "");
      setColor(initial.color || PALETTE[0]);
      setThreshold(initial.alert_threshold ?? 80);
    }
    // eslint-disable-next-line
  }, [open]);

  const submit = () => {
    if (!name.trim()) return toast.error("Name is required");
    onSubmit({ name: name.trim(), proposed: parseFloat(proposed) || 0, color, parent_id: parentId, alert_threshold: parseFloat(threshold) || 0 });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#141a26] border-[#2a364f] text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">Set a name, monthly proposed amount and a spending alert threshold.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Groceries" className="bg-[#0a0e14] border-[#2a364f]" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>Proposed amount</Label>
            <Input data-testid="category-proposed-input" type="number" value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder="0.00" className="bg-[#0a0e14] border-[#2a364f] font-mono-num" />
          </div>
          <div className="space-y-1.5">
            <Label>Alert threshold — warn at {threshold}% of budget</Label>
            <Input data-testid="category-threshold-input" type="number" min="0" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="80" className="bg-[#0a0e14] border-[#2a364f] font-mono-num" />
          </div>
          {!parentId && (
            <div className="space-y-1.5">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {PALETTE.map((c) => (
                  <button key={c} onClick={() => setColor(c)} className="w-7 h-7 rounded-full transition-transform" style={{ background: c, outline: color === c ? "2px solid #fff" : "none", outlineOffset: 2, transform: color === c ? "scale(1.1)" : "none" }} />
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="save-category-button" onClick={submit} className="bg-[#f97316] hover:bg-[#ea580c] text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteCategoryButton = ({ name, onConfirm, testid, trigger }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      {trigger || (
        <Button data-testid={testid} variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-400 hover:bg-red-500/10">
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-[#141a26] border-[#2a364f] text-slate-100">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading">Delete "{name}"?</AlertDialogTitle>
        <AlertDialogDescription className="text-slate-400">
          Sub-categories will also be removed and related transactions moved to Unassigned.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-transparent border-[#2a364f] text-slate-300 hover:bg-[#2a364f]">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
